import {createUnifiedTheme, genPageTheme, palettes, shapes,} from '@backstage/theme';

// Font family - Open Sans from Google Fonts (GasNet)
const fontFamily = '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

// =============================================================================
// LIGHT THEME (GasNet) - white bar with green accent
// =============================================================================

// Brand colors - light variant
const lightColors = {
  // GasNet brand green
  brandGreen: '#40c400',
  // Darker green for active text / highlights
  brandGreenDark: '#2e961d',
  // Neutral grey (icons / secondary text)
  grey: '#5f6368',
  // White bar background (sidebar + header)
  barBackground: '#ffffff',
  // Hover shade on the white bar (light grey)
  hoverBackground: '#f1f3f1',
  // Active item background (pale green pill)
  selectedBackground: '#e8f5e2',
  // Green accent (active icon / indicator)
  barIndicator: '#40c400',
  // Flat white bar background
  barGradient: '#ffffff',
  // Subtle right border on the sidebar / soft shadows
  barShadow: 'inset -1px 0 0 #e7e9e7',
  headerShadow: 'none',
  cardShadow: '0 2px 10px rgba(31, 41, 51, 0.06), 0 1px 3px rgba(31, 41, 51, 0.08)',
  cardShadowHover: '0 8px 24px rgba(31, 41, 51, 0.12)',
  // Pill backgrounds on the white bar
  pillHover: '#f1f3f1',
  pillSelected: '#e8f5e2',
  // Text / icon color on the white bar
  sidebarText: '#3a3d42',
  // Regular text on the page
  textColor: '#1a1a1a',
  // Heading / title color
  titleColor: '#1a1a1a',
  // Submenu background (white popup)
  submenuBackground: '#ffffff',
  // Page / paper backgrounds
  pageBackground: '#f4f5f4',
  paperBackground: '#ffffff',
};

export const lightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: lightColors.brandGreen,
      dark: lightColors.brandGreenDark,
    },
    secondary: {
      main: lightColors.brandGreenDark,
    },
    navigation: {
      background: lightColors.barBackground,
      indicator: lightColors.barIndicator,
      color: lightColors.sidebarText,
      selectedColor: lightColors.sidebarText,
      navItem: {
        hoverBackground: lightColors.hoverBackground,
      },
      submenu: {
        background: lightColors.submenuBackground,
      },
    },
    text: {
      primary: lightColors.textColor,
      secondary: lightColors.grey,
    },
    background: {
      default: lightColors.pageBackground,
      paper: lightColors.paperBackground,
    },
  },
  fontFamily: fontFamily,
  // Typography settings are applied via MuiTypography component overrides
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: [lightColors.paperBackground, lightColors.paperBackground],
      shape: shapes.wave,
      options: { fontColor: lightColors.textColor },
    }),
    documentation: genPageTheme({
      colors: [lightColors.paperBackground, lightColors.paperBackground],
      shape: shapes.wave,
      options: { fontColor: lightColors.textColor },
    }),
    tool: genPageTheme({
      colors: [lightColors.paperBackground, lightColors.paperBackground],
      shape: shapes.wave,
      options: { fontColor: lightColors.textColor },
    }),
    service: genPageTheme({
      colors: [lightColors.paperBackground, lightColors.paperBackground],
      shape: shapes.wave,
      options: { fontColor: lightColors.textColor },
    }),
    website: genPageTheme({
      colors: [lightColors.paperBackground, lightColors.paperBackground],
      shape: shapes.wave,
      options: { fontColor: lightColors.textColor },
    }),
    library: genPageTheme({
      colors: [lightColors.paperBackground, lightColors.paperBackground],
      shape: shapes.wave,
      options: { fontColor: lightColors.textColor },
    }),
    other: genPageTheme({
      colors: [lightColors.paperBackground, lightColors.paperBackground],
      shape: shapes.wave,
      options: { fontColor: lightColors.textColor },
    }),
    app: genPageTheme({
      colors: [lightColors.paperBackground, lightColors.paperBackground],
      shape: shapes.wave,
      options: { fontColor: lightColors.textColor },
    }),
    apis: genPageTheme({
      colors: [lightColors.paperBackground, lightColors.paperBackground],
      shape: shapes.wave,
      options: { fontColor: lightColors.textColor },
    }),
  },
  components: {
    // Sidebar styling - white bar
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          background: lightColors.barGradient,
          borderRight: 'none',
          boxShadow: lightColors.barShadow,
        },
      },
    },
    // Sidebar item styling
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          color: lightColors.sidebarText,
          margin: '2px 10px',
          borderRadius: 10,
          transition: 'background-color 0.2s ease, color 0.2s ease',
          '&:hover': {
            backgroundColor: lightColors.pillHover,
            color: lightColors.sidebarText,
            '& svg': {
              color: lightColors.barIndicator,
            },
          },
        },
        label: {
          color: 'inherit',
          fontFamily: fontFamily,
          fontSize: '16px',
          fontWeight: 600,
        },
        iconContainer: {
          color: 'inherit',
        },
        // Active item: pale green rounded pill with green icon
        selected: {
          backgroundColor: lightColors.pillSelected,
          color: lightColors.brandGreenDark,
          borderRadius: 10,
          fontWeight: 700,
          '& svg': {
            color: lightColors.barIndicator,
          },
          '&:hover': {
            backgroundColor: lightColors.pillSelected,
          },
        },
        // Search field - rounded grey pill (icon + input inside)
        searchRoot: {
          margin: '4px 12px 14px',
          background: lightColors.hoverBackground,
          borderRadius: 22,
          '& > *': {
            margin: 0,
            borderRadius: 22,
          },
        },
        searchField: {
          color: lightColors.grey,
          fontWeight: 500,
        },
        searchFieldHTMLInput: {
          padding: '10px 0',
        },
      },
    },
    // Sidebar submenu (popup menu) styling
    BackstageSidebarSubmenu: {
      styleOverrides: {
        root: {
          backgroundColor: lightColors.submenuBackground,
          borderLeft: `3px solid ${lightColors.brandGreen}`,
          boxShadow: '4px 4px 12px rgba(0, 0, 0, 0.15)',
          '& > div': {
            backgroundColor: lightColors.submenuBackground,
          },
        },
      },
    },
    // Sidebar submenu item styling
    BackstageSidebarSubmenuItem: {
      styleOverrides: {
        label: {
          color: lightColors.sidebarText,
          fontFamily: fontFamily,
          fontSize: '14px',
          fontWeight: 500,
        },
        selected: {
          backgroundColor: lightColors.selectedBackground,
          color: lightColors.sidebarText,
          '&:hover': {
            backgroundColor: lightColors.selectedBackground,
          },
        },
      },
    },
    // Header styling - white with green underline
    BackstageHeader: {
      styleOverrides: {
        header: {
          background: lightColors.barGradient,
          borderBottom: 'none',
          boxShadow: lightColors.headerShadow,
        },
        title: {
          color: lightColors.titleColor,
          fontFamily: fontFamily,
          fontWeight: 700,
          fontSize: '2rem',
          letterSpacing: '-0.01em',
        },
        subtitle: {
          color: lightColors.grey,
          fontFamily: fontFamily,
          fontWeight: 400,
        },
      },
    },
    // Content header styling
    BackstageContentHeader: {
      styleOverrides: {
        title: {
          color: lightColors.textColor,
        },
      },
    },
    // MUI Typography for general text - Open Sans font
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: fontFamily,
        },
        h1: {
          color: lightColors.textColor,
          fontFamily: fontFamily,
          fontWeight: 800,
          fontSize: '3rem',
        },
        h2: {
          color: lightColors.textColor,
          fontFamily: fontFamily,
          fontWeight: 700,
          fontSize: '1.75rem',
        },
        h3: {
          color: lightColors.textColor,
          fontFamily: fontFamily,
          fontWeight: 600,
          fontSize: '1.25rem',
        },
        h4: {
          color: lightColors.textColor,
          fontFamily: fontFamily,
          fontWeight: 500,
        },
        h5: {
          color: lightColors.textColor,
          fontFamily: fontFamily,
          fontWeight: 500,
        },
        h6: {
          color: lightColors.textColor,
          fontFamily: fontFamily,
          fontWeight: 500,
        },
        body1: {
          color: lightColors.textColor,
          fontFamily: fontFamily,
          fontWeight: 400,
          fontSize: '1rem',
          lineHeight: 1.4,
        },
        body2: {
          color: lightColors.textColor,
          fontFamily: fontFamily,
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: 1.35,
        },
      },
    },
    // Global CSS baseline - set Open Sans as default font
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: fontFamily,
        },
      },
    },
    // Labels and form elements
    MuiInputLabel: {
      styleOverrides: {
        root: { color: lightColors.grey },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { color: lightColors.grey },
      },
    },
    // Table headers
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: fontFamily,
        },
        head: {
          color: lightColors.textColor,
          fontFamily: fontFamily,
          fontWeight: 600,
        },
      },
    },
    // Buttons
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: fontFamily,
          fontWeight: 600,
          textTransform: 'none',
          borderRadius: 8,
        },
        containedPrimary: {
          backgroundColor: lightColors.brandGreen,
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(64, 196, 0, 0.35)',
          '&:hover': {
            backgroundColor: lightColors.brandGreenDark,
            boxShadow: '0 4px 14px rgba(64, 196, 0, 0.45)',
          },
        },
      },
    },
    // Paper / Cards - rounded with soft shadow
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: lightColors.cardShadow,
        },
        elevation2: {
          boxShadow: lightColors.cardShadow,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid rgba(31, 41, 51, 0.06)',
          boxShadow: lightColors.cardShadow,
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: lightColors.cardShadowHover,
          },
        },
      },
    },
    // Breadcrumbs
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { color: lightColors.grey },
      },
    },
    // Tabs
    MuiTab: {
      styleOverrides: {
        root: {
          color: lightColors.grey,
          '&.Mui-selected': { color: lightColors.brandGreen },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: lightColors.brandGreen },
      },
    },
    // Catalog filter panel (User list picker) - green instead of grey
    CatalogReactUserListPicker: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(64, 196, 0, 0.08)',
        },
        menuItem: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(64, 196, 0, 0.16)',
            borderLeft: `3px solid ${lightColors.brandGreen}`,
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(64, 196, 0, 0.22)',
          },
        },
      },
    },
  },
});

// =============================================================================
// DARK THEME (GasNet) - dark bar with green accent
// =============================================================================

const darkColors = {
  // Sidebar
  sidebarBg: '#1e2129',
  sidebarHover: '#2a2e38',
  sidebarSelected: '#2a2e38',
  sidebarBorder: '#333842',
  sidebarText: '#c2c6cc',

  // Submenu
  submenuBg: '#252932',
  submenuSel: '#2e961d',

  // Main accent - GasNet green
  accent: '#40c400',
  accentHover: '#2e961d',
  accentLight: '#5bd41f',

  // Content area background
  pageBg: '#16181d',
  paperBg: '#1e2129',
  paperElevated: '#252932',

  // Boundary
  border: '#333842',
  borderLight: '#444a56',

  // Text
  textPrimary: '#e4e6ea',
  textSecondary: '#9aa0aa',
  textMuted: '#6b7280',

  // Header title
  titleColor: '#40c400',
  subtitleColor: '#9aa0aa',

  // Modern gradient + shadows
  barGradient: 'linear-gradient(170deg, #262b35 0%, #1e2129 55%, #141619 100%)',
  barShadow: '2px 0 18px rgba(0, 0, 0, 0.35)',
  headerShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
  // Translucent pill backgrounds on the bar
  pillHover: 'rgba(255, 255, 255, 0.06)',
  pillSelected: 'rgba(64, 196, 0, 0.14)',
};

export const darkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: {
      main: darkColors.accent,
      light: darkColors.accentLight,
      dark: darkColors.accentHover,
    },
    secondary: {
      main: darkColors.textSecondary,
    },
    navigation: {
      background: darkColors.sidebarBg,
      indicator: darkColors.accent,
      color: darkColors.sidebarText,
      selectedColor: '#ffffff',
      navItem: {
        hoverBackground: darkColors.sidebarHover,
      },
      submenu: {
        background: darkColors.submenuBg,
      },
    },
    text: {
      primary: darkColors.textPrimary,
      secondary: darkColors.textSecondary,
    },
    background: {
      default: darkColors.pageBg,
      paper: darkColors.paperBg,
    },
    divider: darkColors.border,
    action: {
      hover: darkColors.sidebarHover,
      selected: darkColors.sidebarSelected,
    },
  },
  fontFamily: fontFamily,
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({
      colors: [darkColors.sidebarBg, darkColors.paperElevated],
      shape: shapes.wave,
      options: { fontColor: darkColors.textPrimary },
    }),
    documentation: genPageTheme({
      colors: [darkColors.sidebarBg, darkColors.paperElevated],
      shape: shapes.wave,
      options: { fontColor: darkColors.textPrimary },
    }),
    tool: genPageTheme({
      colors: [darkColors.sidebarBg, darkColors.paperElevated],
      shape: shapes.wave,
      options: { fontColor: darkColors.textPrimary },
    }),
    service: genPageTheme({
      colors: [darkColors.sidebarBg, darkColors.paperElevated],
      shape: shapes.wave,
      options: { fontColor: darkColors.textPrimary },
    }),
    website: genPageTheme({
      colors: [darkColors.sidebarBg, darkColors.paperElevated],
      shape: shapes.wave,
      options: { fontColor: darkColors.textPrimary },
    }),
    library: genPageTheme({
      colors: [darkColors.sidebarBg, darkColors.paperElevated],
      shape: shapes.wave,
      options: { fontColor: darkColors.textPrimary },
    }),
    other: genPageTheme({
      colors: [darkColors.sidebarBg, darkColors.paperElevated],
      shape: shapes.wave,
      options: { fontColor: darkColors.textPrimary },
    }),
    app: genPageTheme({
      colors: [darkColors.sidebarBg, darkColors.paperElevated],
      shape: shapes.wave,
      options: { fontColor: darkColors.textPrimary },
    }),
    apis: genPageTheme({
      colors: [darkColors.sidebarBg, darkColors.paperElevated],
      shape: shapes.wave,
      options: { fontColor: darkColors.textPrimary },
    }),
  },
  components: {
    // Sidebar
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          background: darkColors.barGradient,
          borderRight: `1px solid ${darkColors.sidebarBorder}`,
          boxShadow: darkColors.barShadow,
        },
      },
    },
    // Sidebar items
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          color: darkColors.sidebarText,
          margin: '2px 10px',
          borderRadius: 10,
          transition: 'background-color 0.2s ease, color 0.2s ease',
          '&:hover': {
            backgroundColor: darkColors.pillHover,
            color: '#ffffff',
            '& svg': { color: darkColors.accent },
          },
        },
        label: {
          color: 'inherit',
          fontFamily: fontFamily,
          fontSize: '16px',
          fontWeight: 600,
        },
        iconContainer: {
          color: 'inherit',
        },
        selected: {
          backgroundColor: darkColors.pillSelected,
          color: '#ffffff',
          borderRadius: 10,
          boxShadow: `inset 3px 0 0 ${darkColors.accent}`,
          '& svg': { color: darkColors.accent },
        },
      },
    },
    // Sidebar submenu
    BackstageSidebarSubmenu: {
      styleOverrides: {
        root: {
          backgroundColor: darkColors.submenuBg,
          borderLeft: `3px solid ${darkColors.accent}`,
          boxShadow: '4px 0 16px rgba(0,0,0,0.5)',
          '& > div': { backgroundColor: darkColors.submenuBg },
        },
      },
    },
    // Sidebar submenu items
    BackstageSidebarSubmenuItem: {
      styleOverrides: {
        label: {
          color: darkColors.sidebarText,
          fontFamily: fontFamily,
          fontSize: '14px',
          fontWeight: 500,
        },
        selected: {
          backgroundColor: darkColors.submenuSel,
          color: '#ffffff',
          '&:hover': { backgroundColor: darkColors.submenuSel },
        },
      },
    },
    // Header - dark with green underline
    BackstageHeader: {
      styleOverrides: {
        header: {
          background: darkColors.barGradient,
          borderBottom: `2px solid ${darkColors.accent}`,
          boxShadow: darkColors.headerShadow,
        },
        title: {
          color: darkColors.titleColor,
          fontFamily: fontFamily,
          fontWeight: 700,
          fontSize: '1.75rem',
        },
        subtitle: {
          color: darkColors.subtitleColor,
          fontFamily: fontFamily,
          fontWeight: 400,
        },
      },
    },
    // Content header
    BackstageContentHeader: {
      styleOverrides: {
        title: {
          color: darkColors.textPrimary,
          fontWeight: 600,
        },
      },
    },
    // Typography
    MuiTypography: {
      styleOverrides: {
        root: { fontFamily: fontFamily },
        h1: {
          color: darkColors.textPrimary,
          fontFamily: fontFamily,
          fontWeight: 800,
          fontSize: '3rem',
        },
        h2: {
          color: darkColors.textPrimary,
          fontFamily: fontFamily,
          fontWeight: 700,
          fontSize: '1.75rem',
        },
        h3: {
          color: darkColors.textPrimary,
          fontFamily: fontFamily,
          fontWeight: 600,
          fontSize: '1.25rem',
        },
        h4: { color: darkColors.textPrimary, fontFamily: fontFamily, fontWeight: 500 },
        h5: { color: darkColors.textPrimary, fontFamily: fontFamily, fontWeight: 500 },
        h6: { color: darkColors.textPrimary, fontFamily: fontFamily, fontWeight: 500 },
        body1: {
          color: darkColors.textPrimary,
          fontFamily: fontFamily,
          fontWeight: 400,
          fontSize: '1rem',
          lineHeight: 1.4,
        },
        body2: {
          color: darkColors.textSecondary,
          fontFamily: fontFamily,
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: 1.35,
        },
      },
    },
    // Global baseline
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: fontFamily,
          backgroundColor: darkColors.pageBg,
        },
      },
    },
    // Paper / Cards
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: darkColors.paperBg,
          backgroundImage: 'none',
          borderColor: darkColors.border,
        },
        elevation1: {
          boxShadow: `0 1px 0 ${darkColors.border}`,
        },
        elevation2: {
          boxShadow: `0 3px 6px rgba(0,0,0,0.4)`,
        },
        outlined: {
          borderColor: darkColors.border,
        },
      },
    },
    // Input labels
    MuiInputLabel: {
      styleOverrides: {
        root: { color: darkColors.textSecondary },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { color: darkColors.textSecondary },
      },
    },
    // Input fields
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: darkColors.paperElevated,
          color: darkColors.textPrimary,
          '& fieldset': { borderColor: darkColors.border },
          '&:hover fieldset': { borderColor: darkColors.borderLight },
          '&.Mui-focused fieldset': { borderColor: darkColors.accent },
        },
        input: {
          color: darkColors.textPrimary,
          '&::placeholder': { color: darkColors.textMuted, opacity: 1 },
        },
        notchedOutline: { borderColor: darkColors.border },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { color: darkColors.textPrimary },
        input: {
          color: darkColors.textPrimary,
          '&::placeholder': { color: darkColors.textMuted, opacity: 1 },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: darkColors.paperElevated,
          color: darkColors.textPrimary,
        },
        input: { color: darkColors.textPrimary },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: { color: darkColors.textPrimary },
        input: { color: darkColors.textPrimary },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: { color: darkColors.textPrimary },
        icon: { color: darkColors.textSecondary },
      },
    },
    // Table
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: fontFamily,
          borderBottomColor: darkColors.border,
          color: darkColors.textPrimary,
        },
        head: {
          color: darkColors.textSecondary,
          fontFamily: fontFamily,
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          backgroundColor: darkColors.paperElevated,
          borderBottom: `1px solid ${darkColors.border}`,
        },
      },
    },
    // Buttons
    MuiButton: {
      styleOverrides: {
        root: {
          fontFamily: fontFamily,
          fontWeight: 500,
          textTransform: 'none',
          borderRadius: '6px',
        },
        containedPrimary: {
          backgroundColor: darkColors.accent,
          color: '#ffffff',
          '&:hover': { backgroundColor: darkColors.accentHover },
        },
        outlinedPrimary: {
          borderColor: darkColors.border,
          color: darkColors.textPrimary,
          '&:hover': {
            borderColor: darkColors.accent,
            backgroundColor: 'rgba(64,196,0,0.08)',
            color: darkColors.accent,
          },
        },
      },
    },
    // Breadcrumbs
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { color: darkColors.textSecondary },
        separator: { color: darkColors.textMuted },
      },
    },
    // Tabs
    MuiTab: {
      styleOverrides: {
        root: {
          color: darkColors.textSecondary,
          fontFamily: fontFamily,
          fontWeight: 500,
          textTransform: 'none',
          '&.Mui-selected': { color: darkColors.accent },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: darkColors.accent },
      },
    },
    // Chips
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: darkColors.paperElevated,
          color: darkColors.textPrimary,
          borderColor: darkColors.border,
          fontFamily: fontFamily,
          fontSize: '0.75rem',
        },
        colorPrimary: {
          backgroundColor: 'rgba(64,196,0,0.15)',
          color: darkColors.accentLight,
          borderColor: 'rgba(64,196,0,0.4)',
        },
      },
    },
    // Dividers
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: darkColors.border },
      },
    },
    // List items
    MuiListItem: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: darkColors.sidebarHover },
        },
      },
    },
    // Cards
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: darkColors.paperBg,
          border: `1px solid ${darkColors.border}`,
          borderRadius: '6px',
          boxShadow: 'none',
        },
      },
    },
    // Catalog filter panel (User list picker) - green instead of grey
    CatalogReactUserListPicker: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(64, 196, 0, 0.10)',
        },
        menuItem: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(64, 196, 0, 0.18)',
            borderLeft: `3px solid ${darkColors.accent}`,
          },
          '&.Mui-selected:hover': {
            backgroundColor: 'rgba(64, 196, 0, 0.24)',
          },
        },
      },
    },
  },
});
